import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import notificationService from '../services/notificationService';

const NotificationContext = createContext({
  notifications: [],
  addNotification: () => {},
  removeNotification: () => {},
  markAsRead: () => {},
  markAllAsRead: () => {},
  clearAll: () => {},
  unreadCount: 0
});

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [toasts, setToasts] = useState([]);

  // Load notifications from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('winglet-notifications');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setNotifications(parsed);
      } catch (error) {
        console.error('Failed to parse saved notifications:', error);
      }
    }
  }, []);

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('winglet-notifications', JSON.stringify(notifications));
  }, [notifications]);

  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: `notif-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`,
      timestamp: new Date()?.toISOString(),
      read: false,
      ...notification
    };

    setNotifications(prev => [newNotification, ...prev]);

    // Also add to toast queue if showToast is true
    if (notification?.showToast !== false) {
      const toast = {
        ...newNotification,
        autoClose: notification?.autoClose !== false,
        duration: notification?.duration || 5000
      };
      setToasts(prev => [...prev, toast]);

      // Auto-remove toast after duration
      if (toast?.autoClose) {
        setTimeout(() => {
          setToasts(prev => prev?.filter(t => t?.id !== toast?.id));
        }, toast?.duration);
      }
    }

    return newNotification?.id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev?.filter(n => n?.id !== id));
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev?.filter(t => t?.id !== id));
  }, []);

  const markAsRead = useCallback((id) => {
    setNotifications(prev => 
      prev?.map(n => n?.id === id ? { ...n, read: true } : n)
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev?.map(n => ({ ...n, read: true })));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    localStorage.removeItem('winglet-notifications');
  }, []);

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  const value = {
    notifications,
    toasts,
    addNotification,
    removeNotification,
    removeToast,
    markAsRead,
    markAllAsRead,
    clearAll,
    unreadCount
  };

  // Set notification service context
  useEffect(() => {
    notificationService?.setContext(value);
  }, [value]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;